import { sentryEnvironmentOf } from "./sentryEnvironment";
import { scrubBrowserEvent } from "./sentryUrlScrub";

import type { BrowserOptions, ErrorEvent, EventHint } from "@sentry/react";

/**
 * Bugsink project DSN. Bugsink is error-tracking only — we send it no
 * transactions, traces or replays (see `integrations` / `tracesSampleRate`).
 */
const BUGSINK_DSN = "https://d8fb283bdd06481687dd0db3de234328@bugsink.digital-now.dev/1";

/**
 * Messages that never describe a defect in this app. Sentry matches these as
 * substrings against the event message *and* the exception value, so each entry
 * only has to be the stable part of the string.
 */
export const IGNORED_ERRORS = [
	// Requests we cancelled ourselves (route change, superseded RTK Query call).
	"AbortError: signal is aborted without reason",
	"AbortError", // Catch-all for AbortErrors
	// Microsoft Outlook Safe Links and the Outlook in-app browser inject their
	// own script into whatever page they open. That script calls a native bridge
	// that is no longer attached and rejects with a bare string, which Sentry
	// reports as "Non-Error promise rejection captured with value: Object Not
	// Found Matching Id:<n>, MethodName:update, ParamCount:4". No frame of ours
	// is on the stack and no user sees anything break; it reaches us only
	// because our notification mails deep-link into the app (see
	// apps/api/src/mail/app-links.ts). The substring covers every Id:<n>.
	"Object Not Found Matching Id",
	// A tab left open across a deploy asks for a lazy chunk the deploy removed.
	// staleChunkReload.ts reloads the tab and the tour's boundary renders nothing
	// meanwhile, so the event describes a stale tab, not a bug. Each browser words
	// it differently: Chrome/Edge, Firefox, Safari (PREC-4387).
	"Failed to fetch dynamically imported module",
	"error loading dynamically imported module",
	"Importing a module script failed",
];

/**
 * Drops events we do not want in Bugsink but that `ignoreErrors` cannot express,
 * because the decision is made on the thrown object rather than on its message.
 */
function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
	const error = hint.originalException as Error;

	if (error && error.name === "AbortError") {
		return null; // Drop this event
	}

	// PREC-4385: the page URL, Referer and breadcrumbs lose the one-time tokens of our public links.
	return scrubBrowserEvent(event);
}

/** Builds the options passed to `Sentry.init()` in index.tsx. */
export function buildSentryInitOptions(
	hostname: string = window.location.hostname
): BrowserOptions {
	return {
		dsn: BUGSINK_DSN,
		environment: sentryEnvironmentOf(hostname),
		beforeSend,
		ignoreErrors: IGNORED_ERRORS,
		// Bugsink is error-tracking only — no tracing/replay integrations.
		integrations: [],
		tracesSampleRate: 0,
	};
}
